import { isAxiosError } from "axios";
import type { AddProjectMemberPayload, AddProjectRequest } from "@/services/api/api-types";
import type { ProjectsUsersAndGroups, TriyoProjectTemplate } from "@/types/models";

/** Sentinel error message meaning the signed-in user may not load the Deal roster. */
export const CREATE_DEAL_ACCESS_DENIED =
  "Your TRIYO account does not have access to create Deals.";

export type DealPriority = "Low" | "Medium" | "High";

export const DEAL_PRIORITIES: DealPriority[] = ["Low", "Medium", "High"];

/** A selectable person or group on the team steps. */
export interface DealPersonOption {
  /** Stable selection key: "u-{UserId}" or "g-{GroupId}". */
  key: string;
  id: number;
  name: string;
  /** Secondary line: role name for users, member count for groups. */
  detail: string;
  isGroup: boolean;
}

/** Flattens the roster response into selectable options, alphabetical, groups last. */
export function normalizeRoster(data: ProjectsUsersAndGroups | null): DealPersonOption[] {
  const users = (data?.UserMembers ?? [])
    .map((member): DealPersonOption | null => {
      const id = member.UserId ?? member.Id;
      if (!id || !member.Name) return null;
      return { key: `u-${id}`, id, name: member.Name, detail: member.Role?.Name ?? "", isGroup: false };
    })
    .filter((option): option is DealPersonOption => option !== null);

  const groups = (data?.GroupMembers ?? [])
    .map((group): DealPersonOption | null => {
      const id = group.GroupId ?? group.Id;
      if (!id || !group.Name) return null;
      const count = Array.isArray(group.Members) ? group.Members.length : 0;
      const detail = count > 0 ? `Group · ${count} member${count === 1 ? "" : "s"}` : "Group";
      return { key: `g-${id}`, id, name: group.Name, detail, isGroup: true };
    })
    .filter((option): option is DealPersonOption => option !== null);

  const byName = (a: DealPersonOption, b: DealPersonOption) => a.name.localeCompare(b.name);
  return [...users.sort(byName), ...groups.sort(byName)];
}

/** Filters options by a case-insensitive query over name and detail. */
export function filterRoster(options: DealPersonOption[], query: string): DealPersonOption[] {
  const q = query.trim().toLowerCase();
  if (!q) return options;
  return options.filter(
    (option) => option.name.toLowerCase().includes(q) || option.detail.toLowerCase().includes(q)
  );
}

/** "YYYY-MM-DD" for a date input, in local time. */
export function toDateInputValue(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Adds days to a "YYYY-MM-DD" value; returns "" for an invalid input. */
export function addDaysToDateInput(value: string, days: number): string {
  const parsed = parseDateInput(value);
  if (!parsed) return "";
  parsed.setDate(parsed.getDate() + days);
  return toDateInputValue(parsed);
}

/** Parses "YYYY-MM-DD" as a local date; null when invalid. */
export function parseDateInput(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

export interface BuildDealPayloadInput {
  name: string;
  description: string;
  /** "YYYY-MM-DD" values from the date inputs. */
  start: string;
  end: string;
  priority: DealPriority;
  template: TriyoProjectTemplate | null;
  owners: DealPersonOption[];
  members: DealPersonOption[];
}

/**
 * Builds the /projects/AddProject body. Owners are also sent as Members
 * (mirroring Triyo.Web, where owners are picked from the member list), with
 * duplicates removed. Dates are sent as timezone-less local midnight; the API
 * normalizes End to end-of-day.
 */
export function buildDealPayload(input: BuildDealPayloadInput): AddProjectRequest {
  const memberByKey = new Map<string, AddProjectMemberPayload>();
  for (const person of [...input.owners, ...input.members]) {
    if (!memberByKey.has(person.key)) {
      memberByKey.set(person.key, person.isGroup ? { GroupId: person.id } : { UserId: person.id });
    }
  }

  const firstUserOwner = input.owners.find((owner) => !owner.isGroup);
  const templateId = input.template?.Id;

  return {
    Name: input.name.trim(),
    Description: input.description.trim(),
    Start: `${input.start}T00:00:00`,
    End: `${input.end}T00:00:00`,
    Status: "Active",
    Priority: input.priority,
    ...(firstUserOwner ? { OwnerId: firstUserOwner.id } : {}),
    Owners: input.owners.map((owner) => ({ Id: owner.id, IsGroup: owner.isGroup })),
    Members: [...memberByKey.values()],
    IsMultiOwner: input.template?.IsMultiOwner ?? true,
    ...(templateId
      ? { ParentTemplateId: templateId, ParentTemplateName: input.template?.ProjectType ?? "" }
      : {}),
  };
}

/** True when the API rejected the caller outright (no Projects/View privilege). */
export function isForbiddenError(error: unknown): boolean {
  return isAxiosError(error) && (error.response?.status === 403 || error.response?.status === 401);
}

/** Human-readable message from an AddProject failure (e.g. duplicate name 400). */
export function parseApiErrorMessage(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const data: unknown = error.response?.data;
    if (typeof data === "string" && data.trim()) return data;
    if (data && typeof data === "object") {
      const message = (data as { Message?: unknown }).Message;
      if (typeof message === "string" && message.trim()) return message;
    }
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}
